import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { todayStr, fmtTime } from "@/components/Pager";
import { Sunrise, BookOpenText, AlertTriangle } from "lucide-react";

const yesterday = () => { const d = new Date(); d.setDate(d.getDate() - (d.getDay() === 1 ? 2 : 1)); return d.toLocaleDateString("en-CA"); };

export function CoordinatorSummary() {
  const [day, setDay] = useState(yesterday());
  const [missing, setMissing] = useState([]);
  const [overdue, setOverdue] = useState([]);
  useEffect(() => { api.get("/monitor/teachers", { params: { day } }).then((r) => setMissing(r.data.missing)).catch(() => setMissing([])); }, [day]);
  useEffect(() => { api.get("/tasks", { params: { scope: "all", status: "overdue", limit: 50 } }).then((r) => setOverdue(r.data.items)).catch(() => setOverdue([])); }, []);
  const byTeacher = overdue.reduce((m, t) => ({ ...m, [t.teacher_name]: [...(m[t.teacher_name] || []), t] }), {});
  return (
    <div data-testid="coordinator-summary" className="bg-white rounded-2xl shadow-sm p-4 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Sunrise size={18} className="text-amber-500" />
        <h2 className="font-display font-bold text-slate-900 text-lg">Daily summary</h2>
        <div className="ml-auto inline-flex rounded-full bg-violet-50 p-0.5">
          {[["Previous day", yesterday()], ["Today", todayStr()]].map(([l, v]) => (
            <button key={l} data-testid={`summary-day-${l.split(" ")[0].toLowerCase()}`} onClick={() => setDay(v)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${day === v ? "bg-violet-600 text-white" : "text-violet-700"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-1.5 text-sm font-semibold text-rose-700 mb-2"><BookOpenText size={15} /> Missing diaries ({missing.length})</div>
          {missing.length === 0 ? <p data-testid="summary-no-missing" className="text-xs text-slate-500">Every assigned class has a diary for this day.</p> : (
            <div className="space-y-1.5">{missing.map((m) => (
              <div key={m.teacher_id + m.class_name + m.section} data-testid={`summary-missing-${m.teacher_id}-${m.class_name}`} className="flex items-center gap-2 text-sm bg-rose-50 rounded-lg px-3 py-1.5">
                <span className="font-semibold flex-1 truncate">{m.teacher_name}</span><span>{m.class_name}-{m.section}</span>
                <span className="text-[11px] text-rose-700">{m.status === "draft" ? "Draft only" : "Not submitted"}</span>
              </div>))}</div>
          )}
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-sm font-semibold text-orange-700 mb-2"><AlertTriangle size={15} /> Overdue tasks ({overdue.length})</div>
          {overdue.length === 0 ? <p data-testid="summary-no-overdue" className="text-xs text-slate-500">No overdue tasks.</p> : (
            <div className="space-y-1.5">{Object.entries(byTeacher).map(([name, ts]) => (
              <div key={name} data-testid={`summary-overdue-${name}`} className="text-sm bg-orange-50 rounded-lg px-3 py-1.5">
                <div className="font-semibold">{name} <span className="text-xs text-orange-700">• {ts.length} overdue</span></div>
                {ts.slice(0, 3).map((t) => <div key={t.id} className="text-xs text-slate-600 truncate">{t.title} — due {t.due_date}{t.due_time ? ` ${t.due_time}` : ""}{t.started_at ? ` • started ${fmtTime(t.started_at)}` : ""}</div>)}
              </div>))}</div>
          )}
        </div>
      </div>
      <div className="flex gap-3 text-xs font-semibold">
        <Link to="/monitor" data-testid="summary-open-monitor" className="text-violet-700 hover:underline">Open Teacher Monitoring →</Link>
        <Link to="/tasks" data-testid="summary-open-tasks" className="text-violet-700 hover:underline">Open Teacher Tasks →</Link>
      </div>
    </div>
  );
}
